import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';

// URL base del backend
const API_BASE = process.env.REACT_APP_NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PRODUCTION : 'http://localhost:3001';

const BrandingSettings = ({ onSuccess }) => {
  // =====================================================
  // ESTADO
  // =====================================================
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [config, setConfig] = useState(null);

  // Referencias para inputs de archivo
  const inputLogoPrincipal = useRef(null);
  const inputLogoSecundario = useRef(null);

  // Colores predefinidos
  const coloresPredefinidos = [
    { nombre: 'Azul Oscuro', valor: '#1a365d' },
    { nombre: 'Azul', valor: '#2563eb' },
    { nombre: 'Índigo', valor: '#4f46e5' },
    { nombre: 'Púrpura', valor: '#7c3aed' },
    { nombre: 'Rosa', valor: '#db2777' },
    { nombre: 'Rojo', valor: '#dc2626' },
    { nombre: 'Naranja', valor: '#d97706' },
    { nombre: 'Verde', valor: '#059669' },
    { nombre: 'Teal', valor: '#0d9488' },
    { nombre: 'Gris', valor: '#4b5563' }
  ];

  // =====================================================
  // CARGAR CONFIGURACIÓN
  // =====================================================
  useEffect(() => {
    cargarConfiguracion();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const correo = localStorage.getItem('correo');
      
      const response = await fetch(`${API_BASE}/api/config/sistema/personalizacion`, {
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setConfig(data.data || {});
      } else {
        setError(data.error || 'Error al cargar configuración');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // HANDLERS
  // =====================================================
  const handleChange = (campo, valor) => {
    setConfig(prev => ({ ...prev, [campo]: valor }));
  };

  // =====================================================
  // SUBIR LOGO
  // =====================================================
  const handleSubirLogo = async (tipo, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Solo se permiten archivos de imagen');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('El archivo no debe superar 2MB');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        
        try {
          setGuardando(true);
          const correo = localStorage.getItem('correo');
          
          const response = await fetch(`${API_BASE}/api/config/sistema/logo/${tipo}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'usuario-correo': correo || ''
            },
            body: JSON.stringify({ imagen: base64 })
          });

          const data = await response.json();

          if (data.success) {
            setConfig(prev => ({ ...prev, [tipo]: base64 }));
            onSuccess?.('Logo actualizado correctamente');
          } else {
            setError(data.error || 'Error al subir logo');
          }
        } catch (err) {
          setError('Error de conexión');
        } finally {
          setGuardando(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Error al procesar imagen');
    }
  };

  const handleEliminarLogo = async (tipo) => {
    if (!window.confirm('¿Estás seguro de eliminar este logo?')) return;

    try {
      setGuardando(true);
      const correo = localStorage.getItem('correo');
      
      const response = await fetch(`${API_BASE}/api/config/sistema/logo/${tipo}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        },
        body: JSON.stringify({ imagen: null })
      });

      const data = await response.json();

      if (data.success) {
        setConfig(prev => ({ ...prev, [tipo]: null }));
        onSuccess?.('Logo eliminado');
      } else {
        setError(data.error || 'Error al eliminar logo');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setGuardando(false);
    }
  };

  // =====================================================
  // GUARDAR CONFIGURACIÓN
  // =====================================================
  const handleGuardar = async () => {
    try {
      setGuardando(true);
      setError(null);

      const correo = localStorage.getItem('correo');

      const response = await fetch(`${API_BASE}/api/config/sistema/personalizacion`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        },
        body: JSON.stringify({
          nombreEmpresa: config.nombreEmpresa,
          slogan: config.slogan,
          colorPrimario: config.colorPrimario,
          colorSecundario: config.colorSecundario
        })
      });

      const data = await response.json();

      if (data.success) {
        onSuccess?.('Personalización guardada correctamente');
      } else {
        setError(data.error || 'Error al guardar');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setGuardando(false);
    }
  };

  // =====================================================
  // RENDER - LOADING
  // =====================================================
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Cargando personalización...</span>
      </div>
    );
  }

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================
  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h5 className="mb-1">
          <i className="bi bi-palette text-primary me-2"></i>
          Personalización
        </h5>
        <small className="text-muted">Configura los logos y colores del sistema</small>
      </div>

      {/* Mensaje de error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {config && (
        <>
          {/* Información de la Empresa */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h6 className="mb-0">
                <i className="bi bi-building me-2"></i>
                Información de la Empresa
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nombre de la empresa</Form.Label>
                    <Form.Control
                      type="text"
                      value={config.nombreEmpresa || ''}
                      onChange={(e) => handleChange('nombreEmpresa', e.target.value)}
                      placeholder="Ej: Colombiana de Trasplantes S.A.S."
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Slogan (opcional)</Form.Label>
                    <Form.Control
                      type="text"
                      value={config.slogan || ''}
                      onChange={(e) => handleChange('slogan', e.target.value)}
                      placeholder="Ej: Salvando vidas"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Logos */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h6 className="mb-0">
                <i className="bi bi-image me-2"></i>
                Logos
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                {/* Logo Principal */}
                <Col md={6} className="mb-3">
                  <Form.Label>Logo Principal</Form.Label>
                  <div className="border rounded p-3 text-center bg-light" style={{ minHeight: '150px' }}>
                    {config.logoPrincipal ? (
                      <div className="position-relative d-inline-block">
                        <img 
                          src={config.logoPrincipal} 
                          alt="Logo Principal"
                          style={{ maxHeight: '100px', maxWidth: '100%' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => handleEliminarLogo('logoPrincipal')}
                        >
                          <i className="bi bi-x"></i>
                        </Button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => inputLogoPrincipal.current?.click()}
                        style={{ cursor: 'pointer' }}
                        className="py-4"
                      >
                        <i className="bi bi-cloud-upload fs-1 text-muted d-block mb-2"></i>
                        <span className="text-muted">Haz clic para subir</span>
                        <br />
                        <small className="text-muted">PNG, JPG (máx. 2MB)</small>
                      </div>
                    )}
                  </div>
                  <input
                    ref={inputLogoPrincipal}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleSubirLogo('logoPrincipal', e.target.files[0])}
                    className="d-none"
                  />
                  {config.logoPrincipal && (
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      className="mt-2 w-100"
                      onClick={() => inputLogoPrincipal.current?.click()}
                    >
                      <i className="bi bi-arrow-repeat me-2"></i>
                      Cambiar logo
                    </Button>
                  )}
                </Col>

                {/* Logo Secundario */}
                <Col md={6} className="mb-3">
                  <Form.Label>Logo Secundario <small className="text-muted">(Ej: Vigilado Supersalud)</small></Form.Label>
                  <div className="border rounded p-3 text-center bg-light" style={{ minHeight: '150px' }}>
                    {config.logoSecundario ? (
                      <div className="position-relative d-inline-block">
                        <img 
                          src={config.logoSecundario} 
                          alt="Logo Secundario"
                          style={{ maxHeight: '100px', maxWidth: '100%' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => handleEliminarLogo('logoSecundario')}
                        >
                          <i className="bi bi-x"></i>
                        </Button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => inputLogoSecundario.current?.click()}
                        style={{ cursor: 'pointer' }}
                        className="py-4"
                      >
                        <i className="bi bi-cloud-upload fs-1 text-muted d-block mb-2"></i>
                        <span className="text-muted">Haz clic para subir</span>
                        <br />
                        <small className="text-muted">PNG, JPG (máx. 2MB)</small>
                      </div>
                    )}
                  </div>
                  <input
                    ref={inputLogoSecundario}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleSubirLogo('logoSecundario', e.target.files[0])}
                    className="d-none"
                  />
                  {config.logoSecundario && (
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      className="mt-2 w-100"
                      onClick={() => inputLogoSecundario.current?.click()}
                    >
                      <i className="bi bi-arrow-repeat me-2"></i>
                      Cambiar logo
                    </Button>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Colores */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h6 className="mb-0">
                <i className="bi bi-palette2 me-2"></i>
                Colores
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                {/* Color Primario */}
                <Col md={6} className="mb-3">
                  <Form.Label>Color Primario</Form.Label>
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div 
                      className="rounded shadow-sm"
                      style={{ 
                        width: '50px', 
                        height: '50px', 
                        backgroundColor: config.colorPrimario || '#1a365d'
                      }}
                    />
                    <Form.Control
                      type="text"
                      value={config.colorPrimario || '#1a365d'}
                      onChange={(e) => handleChange('colorPrimario', e.target.value)}
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {coloresPredefinidos.map(color => (
                      <button
                        key={color.valor}
                        type="button"
                        onClick={() => handleChange('colorPrimario', color.valor)}
                        className="btn p-0 rounded-circle"
                        style={{ 
                          width: '28px', 
                          height: '28px', 
                          backgroundColor: color.valor,
                          border: config.colorPrimario === color.valor ? '3px solid #000' : '2px solid #ddd'
                        }}
                        title={color.nombre}
                      />
                    ))}
                  </div>
                </Col>

                {/* Color Secundario */}
                <Col md={6} className="mb-3">
                  <Form.Label>Color Secundario</Form.Label>
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div 
                      className="rounded shadow-sm"
                      style={{ 
                        width: '50px', 
                        height: '50px', 
                        backgroundColor: config.colorSecundario || '#667eea'
                      }}
                    />
                    <Form.Control
                      type="text"
                      value={config.colorSecundario || '#667eea'}
                      onChange={(e) => handleChange('colorSecundario', e.target.value)}
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {coloresPredefinidos.map(color => (
                      <button
                        key={color.valor}
                        type="button"
                        onClick={() => handleChange('colorSecundario', color.valor)}
                        className="btn p-0 rounded-circle"
                        style={{ 
                          width: '28px', 
                          height: '28px', 
                          backgroundColor: color.valor,
                          border: config.colorSecundario === color.valor ? '3px solid #000' : '2px solid #ddd'
                        }}
                        title={color.nombre}
                      />
                    ))}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Vista Previa */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h6 className="mb-0">
                <i className="bi bi-eye me-2"></i>
                Vista Previa
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              <div 
                className="p-4 text-white d-flex justify-content-between align-items-center"
                style={{ backgroundColor: config.colorPrimario || '#1a365d' }}
              >
                <div className="d-flex align-items-center">
                  {config.logoPrincipal && (
                    <img 
                      src={config.logoPrincipal} 
                      alt="Logo"
                      className="me-3"
                      style={{ height: '40px' }}
                    />
                  )}
                  <div>
                    <h5 className="mb-0">{config.nombreEmpresa || 'Nombre de la empresa'}</h5>
                    {config.slogan && (
                      <small className="opacity-75">{config.slogan}</small>
                    )}
                  </div>
                </div>
                <Button 
                  style={{ backgroundColor: config.colorSecundario || '#667eea', border: 'none' }}
                >
                  Botón ejemplo
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Botón Guardar */}
          <div className="d-flex justify-content-end">
            <Button 
              variant="primary" 
              size="lg"
              onClick={handleGuardar}
              disabled={guardando}
            >
              {guardando ? (
                <><Spinner size="sm" className="me-2" />Guardando...</>
              ) : (
                <><i className="bi bi-check-lg me-2"></i>Guardar cambios</>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default BrandingSettings;
